import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { List, ListColumn } from '~/components/common/List'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  SequentialSelect,
  SequentialSelectStep,
  SequentialSelectStepItem
} from '~/components/common/SequentialSelect'
import { Typography } from '~/components/common/Typography'
import { UploadFile } from '~/components/common/UploadFile'
import { XmlFileUploadLeftIcon } from '~/constants/icon'
import { useFolderPicker } from '~/hooks/useFolderPicker'
import { uploadFile as uploadOrgFile } from '~/services/organizationService'
import { RepositoryBranch } from '~/types/Conversations'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { BetterToast } from '~/utils/toast'

export type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error'

export interface UploadedRepo {
  id: number
  name: string
  size: string
  originalFilename: string
  tarFile?: File
  uploadStatus?: UploadStatus
  s3Path?: string
}

interface RepoSelectModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: {
    repositoryBranches: RepositoryBranch[]
    uploadedRepos: UploadedRepo[]
  }) => void
  repositories: Array<{ conn_type: string; index_name: string; label: string }>
  getBranches: (conn_type: string, index_name: string) => string[]
  title?: string
  initialSelectedRepos?: RepositoryBranch[]
  initialUploadedRepos?: UploadedRepo[]
  /** Workspace UUID for S3 uploads - if provided, files are uploaded immediately */
  workspaceUuid?: string
  /** If true, disable confirm until at least one repo or upload is selected. */
  requireSelection?: boolean
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Uploaded Repos Component
interface UploadedReposProps {
  uploadedRepos: UploadedRepo[]
  setUploadedRepos: (repos: UploadedRepo[]) => void
}

// Loading spinner component
const LoadingSpinner = () => (
  <svg
    className='animate-spin h-4 w-4 text-blue-500'
    xmlns='http://www.w3.org/2000/svg'
    fill='none'
    viewBox='0 0 24 24'
  >
    <circle
      className='opacity-25'
      cx='12'
      cy='12'
      r='10'
      stroke='currentColor'
      strokeWidth='4'
    ></circle>
    <path
      className='opacity-75'
      fill='currentColor'
      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
    ></path>
  </svg>
)

// Checkmark icon for completed uploads
const CheckmarkIcon = () => (
  <svg
    className='h-4 w-4 text-green-500'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 20 20'
    fill='currentColor'
  >
    <path
      fillRule='evenodd'
      d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
      clipRule='evenodd'
    />
  </svg>
)

// Error icon for failed uploads
const ErrorIcon = () => (
  <svg
    className='h-4 w-4 text-red-500'
    xmlns='http://www.w3.org/2000/svg'
    viewBox='0 0 20 20'
    fill='currentColor'
  >
    <path
      fillRule='evenodd'
      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
      clipRule='evenodd'
    />
  </svg>
)

const UploadedRepos: React.FC<UploadedReposProps> = ({ uploadedRepos, setUploadedRepos }) => {
  const repoItems = uploadedRepos

  if (repoItems.length === 0) return null

  const columns: ListColumn[] = [
    {
      label: 'Repository',
      flex: 1,
      align: 'left'
    },
    {
      label: '',
      width: '80px',
      align: 'right'
    }
  ]

  const values = repoItems.map(repo => [
    <div key={`repo-${repo.id}`} className='flex flex-col gap-1 min-w-0'>
      <div className='flex items-center gap-2'>
        <Typography
          fontFamily='geist'
          fontSize='bodyMdMedium'
          fontWeight='medium'
          className='text-tokens-neutral-text-primary truncate'
        >
          {repo.name}
        </Typography>
        {/* Upload status indicator */}
        {repo.uploadStatus === 'uploading' && <LoadingSpinner />}
        {(repo.uploadStatus === 'completed' || (!repo.uploadStatus && repo.s3Path)) && (
          <CheckmarkIcon />
        )}
        {repo.uploadStatus === 'error' && <ErrorIcon />}
      </div>
      <div className='flex items-center gap-2'>
        <Typography
          fontFamily='geistMono'
          fontSize='labelSm'
          fontWeight='semibold'
          className='text-tokens-neutral-text-tertiary'
        >
          {repo.size}
        </Typography>
        {repo.uploadStatus === 'uploading' && (
          <Typography fontFamily='geistMono' fontSize='labelSm' className='text-blue-500'>
            Uploading...
          </Typography>
        )}
        {repo.uploadStatus === 'error' && (
          <Typography fontFamily='geistMono' fontSize='labelSm' className='text-red-500'>
            Upload failed
          </Typography>
        )}
      </div>
    </div>,
    <button
      key={`actions-${repo.id}`}
      disabled={repo.uploadStatus === 'uploading'}
      onClick={() => {
        setUploadedRepos(uploadedRepos.filter(uploadedRepo => uploadedRepo.id !== repo.id))
      }}
      className='flex w-[36px] h-[36px] p-0 items-center justify-center rounded-lg hover:bg-[rgba(0,0,0,0.04)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
      title='Remove'
    >
      <svg
        width='20'
        height='20'
        viewBox='0 0 20 20'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          d='M7.03125 3.59375H6.875C6.96094 3.59375 7.03125 3.52344 7.03125 3.4375V3.59375H12.9688V3.4375C12.9688 3.52344 13.0391 3.59375 13.125 3.59375H12.9688V5H14.375V3.4375C14.375 2.74805 13.8145 2.1875 13.125 2.1875H6.875C6.18555 2.1875 5.625 2.74805 5.625 3.4375V5H7.03125V3.59375ZM16.875 5H3.125C2.7793 5 2.5 5.2793 2.5 5.625V6.25C2.5 6.33594 2.57031 6.40625 2.65625 6.40625H3.83594L4.31836 16.6211C4.34961 17.2871 4.90039 17.8125 5.56641 17.8125H14.4336C15.1016 17.8125 15.6504 17.2891 15.6816 16.6211L16.1641 6.40625H17.3438C17.4297 6.40625 17.5 6.33594 17.5 6.25V5.625C17.5 5.2793 17.2207 5 16.875 5ZM14.2832 16.4062H5.7168L5.24414 6.40625H14.7559L14.2832 16.4062Z'
          fill='black'
          fillOpacity='0.45'
        />
      </svg>
    </button>
  ])

  return (
    <div className='flex flex-col gap-4 w-full'>
      <div className='px-4'>
        <Typography
          fontFamily='geistMono'
          fontSize='labelMd'
          fontWeight='semibold'
          className='text-tokens-neutral-text-secondary'
        >
          Uploaded Repos
        </Typography>
      </div>
      <List columns={columns} values={values} headerClassName='hidden' />
    </div>
  )
}

export function RepoSelectModal({
  isOpen,
  onClose,
  onConfirm,
  repositories,
  getBranches,
  title = 'Add Repository',
  initialSelectedRepos = [],
  initialUploadedRepos = [],
  workspaceUuid,
  requireSelection = true
}: RepoSelectModalProps) {
  const [activeTab, setActiveTab] = useState<'select' | 'upload'>('select')
  const [selectedRepos, setSelectedRepos] = useState<RepositoryBranch[]>([])
  const [uploadedRepos, setUploadedRepos] = useState<UploadedRepo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const prevIsOpenRef = useRef(isOpen)

  // Track component mount status to prevent state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Sync state when modal opens (transitions from closed to open)
  useEffect(() => {
    // Only sync when modal transitions from closed to open
    if (isOpen && !prevIsOpenRef.current) {
      setSelectedRepos(initialSelectedRepos)
      setUploadedRepos(initialUploadedRepos)
    }

    prevIsOpenRef.current = isOpen
  }, [isOpen, initialSelectedRepos, initialUploadedRepos])

  const { pickFolder } = useFolderPicker()

  // Start S3 upload for a repo
  const startUpload = useCallback(
    (repoId: number, tarFile: File) => {
      if (!workspaceUuid) return

      uploadOrgFile(workspaceUuid, tarFile, {
        onComplete: (s3Path: string) => {
          if (isMountedRef.current) {
            setUploadedRepos(prev =>
              prev.map(r =>
                r.id === repoId ? { ...r, uploadStatus: 'completed' as UploadStatus, s3Path } : r
              )
            )
          }
        },
        onError: (error: string) => {
          if (isMountedRef.current) {
            setUploadedRepos(prev =>
              prev.map(r => (r.id === repoId ? { ...r, uploadStatus: 'error' as UploadStatus } : r))
            )
            BetterToast.error(`Upload failed: ${error}`)
          }
        }
      })
    },
    [workspaceUuid]
  )

  const handleFolderUpload = async () => {
    if (isLoading) return
    setIsLoading(true)
    try {
      const result = await pickFolder('Select a directory to upload')
      if (!result) return

      const { tarFile, folderName, originalFilename } = result
      const repoId = Date.now()

      const newRepo: UploadedRepo = {
        id: repoId,
        name: folderName,
        size: formatFileSize(tarFile.size),
        originalFilename,
        tarFile,
        uploadStatus: workspaceUuid ? 'uploading' : 'pending'
      }
      setUploadedRepos(prev => [...prev, newRepo])

      if (workspaceUuid) {
        startUpload(repoId, tarFile)
      } else {
        BetterToast.success(`Directory "${folderName}" prepared for upload`)
      }
    } catch (error) {
      BetterToast.error(
        `Failed to process folder: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleSequentialComplete = (selections: SequentialSelectStepItem[]) => {
    const repoSelection = selections[0]
    const branchSelection = selections[1]
    const [conn_type, repository] = repoSelection.value.split(':')
    const repositoryBranch = {
      conn_type,
      repository,
      branch: branchSelection.value
    }
    setSelectedRepos(prev => [...prev, repositoryBranch])
  }

  // Check if any uploads are in progress
  const hasUploadsInProgress = useMemo(
    () => uploadedRepos.some(r => r.uploadStatus === 'uploading'),
    [uploadedRepos]
  )

  // Check if any uploads failed
  const hasFailedUploads = useMemo(
    () => uploadedRepos.some(r => r.uploadStatus === 'error'),
    [uploadedRepos]
  )

  // Validate selection: uploads must be completed; selection requirement is configurable.
  const isValidSelection = useMemo(() => {
    const allUploadsReady =
      !workspaceUuid || uploadedRepos.every(r => r.uploadStatus === 'completed')
    const hasAnySelection = selectedRepos.length > 0 || uploadedRepos.length > 0
    const selectionSatisfied = requireSelection ? hasAnySelection : true
    return allUploadsReady && !hasFailedUploads && selectionSatisfied
  }, [uploadedRepos, workspaceUuid, hasFailedUploads, selectedRepos.length, requireSelection])

  const handleConfirm = useCallback(() => {
    if (hasUploadsInProgress) return
    onConfirm({
      repositoryBranches: selectedRepos,
      uploadedRepos
    })
    // Close modal immediately since uploads are already done
    onClose()
  }, [hasUploadsInProgress, onConfirm, selectedRepos, uploadedRepos, onClose])

  const handleClose = useCallback(() => {
    // Don't allow closing if uploads are in progress
    if (hasUploadsInProgress) return
    // Don't reset state, just close the modal
    // State will be synced when modal reopens
    onClose()
  }, [hasUploadsInProgress, onClose])

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      hideCloseButton={hasUploadsInProgress}
      disableOutsideClick={hasUploadsInProgress}
      className='w-[640px] h-[629px]'
    >
      <div className='h-full flex flex-col'>
        <div className='flex items-center justify-between px-6 py-4 border-b border-tokens-neutral-border-2'>
          <Typography
            fontFamily='geist'
            fontSize='titleSm'
            fontWeight='bold'
            className='text-tokens-neutral-text-primary'
          >
            {title}
          </Typography>
        </div>

        {/* Segmented Control */}
        <div className='flex justify-center pt-6 px-6'>
          <div className='bg-tokens-neutral-bg-grey-2 p-1 rounded-xl flex'>
            <button
              onClick={() => setActiveTab('select')}
              className={`px-6 py-2 rounded-lg transition-all ${
                activeTab === 'select'
                  ? 'bg-white shadow-segment text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-quaternary'
              }`}
            >
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                Select Repo
              </Typography>
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`px-6 py-2 rounded-lg transition-all ${
                activeTab === 'upload'
                  ? 'bg-white shadow-segment text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-quaternary'
              }`}
            >
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                Upload Repos
              </Typography>
            </button>
          </div>
        </div>

        <ModalBody className={'px-[60px] flex-1 overflow-y-auto'}>
          {activeTab === 'select' && (
            <>
              <div>
                <SequentialSelect
                  step={(() => {
                    const repositoryStep: SequentialSelectStep = {
                      label: 'Repository',
                      itemsLabel: 'Select Repository',
                      items: []
                    }

                    const uniqueRepos = repositories.filter(
                      (repo, index, self) =>
                        index ===
                        self.findIndex(
                          t => t.conn_type === repo.conn_type && t.index_name === repo.index_name
                        )
                    )

                    repositoryStep.items = uniqueRepos
                      .filter(repo => {
                        return !selectedRepos.some(
                          selected =>
                            `${selected.conn_type}:${selected.repository}` ===
                            `${repo.conn_type}:${repo.index_name}`
                        )
                      })
                      .map(repo => {
                        const branches = getBranches(repo.conn_type, repo.index_name)

                        const branchStep: SequentialSelectStep = {
                          label: 'Branch',
                          itemsLabel: 'Select Branch',
                          prev: repositoryStep,
                          items: branches.map(branch => ({
                            value: branch,
                            label: branch
                          }))
                        }

                        const repoValue = `${repo.conn_type}:${repo.index_name}`
                        return {
                          value: repoValue,
                          label: repo.label,
                          next: branchStep
                        }
                      })

                    return repositoryStep
                  })()}
                  onComplete={handleSequentialComplete}
                  placeholder='Search or select...'
                />
              </div>

              {selectedRepos.length > 0 && (
                <div className='box-border content-stretch flex flex-col gap-4 items-start justify-start relative w-full mt-8'>
                  <div className='box-border content-stretch flex flex-col gap-2.5 items-start justify-start px-6 py-0 relative shrink-0'>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      fontWeight='semibold'
                      className='text-tokens-neutral-text-secondary'
                    >
                      Repositories Selected
                    </Typography>
                  </div>

                  <div className='bg-[#F9F9F9] box-border content-stretch flex flex-col gap-2 items-start justify-start px-6 py-4 relative rounded-3xl shrink-0 w-full'>
                    {selectedRepos.map((repoBranch, index) => (
                      <Fragment key={`${repoBranch.repository}-${repoBranch.branch}`}>
                        {index > 0 && (
                          <div className='w-full h-[1px] pointer-events-none bg-tokens-neutral-border-2' />
                        )}
                        <div className='group box-border content-stretch flex flex-col items-start justify-start relative shrink-0 w-full hover:bg-gray-50 transition-colors rounded-lg'>
                          <div className='box-border content-stretch flex flex-col items-start justify-start p-0 relative shrink-0 w-full'>
                            <div className='box-border content-stretch flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 w-full'>
                              <div className='box-border content-stretch flex flex-row gap-6 items-center justify-start px-2 py-0 relative shrink-0 w-full'>
                                <div className='basis-0 box-border content-stretch flex flex-col grow items-start justify-center min-h-px min-w-px px-0 py-1 relative shrink-0'>
                                  <div className='box-border content-stretch flex flex-col gap-6 items-center justify-start p-0 relative shrink-0 w-full'>
                                    <div className='box-border content-stretch flex flex-col gap-1 items-start justify-start p-0 relative shrink-0 w-full'>
                                      <div
                                        className='font-medium leading-[0] min-w-full relative shrink-0 text-[14px] text-[rgba(0,0,0,0.88)] text-left'
                                        style={{ width: 'min-content' }}
                                      >
                                        <p className='block leading-[22px]'>
                                          {extractFileDisplayName(repoBranch.repository)} -{' '}
                                          {repoBranch.branch}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    const updatedBranches = selectedRepos.filter(
                                      rb =>
                                        !(
                                          rb.repository === repoBranch.repository &&
                                          rb.branch === repoBranch.branch
                                        )
                                    )
                                    setSelectedRepos(updatedBranches)
                                  }}
                                  className='opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-2 rounded-lg hover:bg-[rgba(0,0,0,0.04)] flex items-center justify-center'
                                  title='Remove'
                                >
                                  <svg
                                    width='20'
                                    height='20'
                                    viewBox='0 0 20 20'
                                    fill='none'
                                    xmlns='http://www.w3.org/2000/svg'
                                  >
                                    <path
                                      d='M7.03125 3.59375H6.875C6.96094 3.59375 7.03125 3.52344 7.03125 3.4375V3.59375H12.9688V3.4375C12.9688 3.52344 13.0391 3.59375 13.125 3.59375H12.9688V5H14.375V3.4375C14.375 2.74805 13.8145 2.1875 13.125 2.1875H6.875C6.18555 2.1875 5.625 2.74805 5.625 3.4375V5H7.03125V3.59375ZM16.875 5H3.125C2.7793 5 2.5 5.2793 2.5 5.625V6.25C2.5 6.33594 2.57031 6.40625 2.65625 6.40625H3.83594L4.31836 16.6211C4.34961 17.2871 4.90039 17.8125 5.56641 17.8125H14.4336C15.1016 17.8125 15.6504 17.2891 15.6816 16.6211L16.1641 6.40625H17.3438C17.4297 6.40625 17.5 6.33594 17.5 6.25V5.625C17.5 5.2793 17.2207 5 16.875 5ZM14.2832 16.4062H5.7168L5.24414 6.40625H14.7559L14.2832 16.4062Z'
                                      fill='black'
                                      fillOpacity='0.45'
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Fragment>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'upload' && (
            <div className='py-4'>
              <div onClick={handleFolderUpload} role='button'>
                <UploadFile
                  className='w-full'
                  variant='with-toggle'
                  uploadState={{
                    status: 'default',
                    progress: 0,
                    error: undefined,
                    file: {
                      name: 'test',
                      size: 1000
                    }
                  }}
                  defaultIcon={<XmlFileUploadLeftIcon />}
                  mainTitle='Upload Directory'
                  description={
                    isLoading
                      ? 'Analyzing and compressing project...'
                      : 'Select a directory to upload'
                  }
                  disabled={isLoading}
                />
              </div>
              {uploadedRepos.length > 0 && (
                <div className='mt-6'>
                  <UploadedRepos
                    uploadedRepos={uploadedRepos}
                    setUploadedRepos={setUploadedRepos}
                  />
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant='outlined' onClick={handleClose} isDisabled={hasUploadsInProgress}>
            Cancel
          </Button>
          <Button
            variant='primary'
            isDisabled={!isValidSelection || hasUploadsInProgress}
            onClick={handleConfirm}
          >
            {hasUploadsInProgress ? 'Uploading...' : 'Confirm'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  )
}

export default RepoSelectModal
